import { useCallback, useEffect } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router';
import { useBeforeUnload } from 'react-router-dom';

/**
 * Hook for blocking navigation when a form has unsaved changes.
 * Handles both in-app navigation (sidebar clicks, programmatic navigation)
 * and browser navigation (refresh, close tab, back button).
 *
 * @param options - Configuration options
 * @param options.isDirty - Whether the form has unsaved changes
 * @param options.onNavigationAttempt - Callback when navigation is blocked (for showing confirmation dialog)
 *
 * @returns Object containing the blocker instance
 *
 * @example
 * ```tsx
 * const navigationDialog = useConfirmationDialog({
 *   onConfirm: () => blocker.proceed(),
 *   onCancel: () => blocker.reset(),
 * });
 *
 * const { blocker } = useFormNavigation({
 *   isDirty: formState.isDirty,
 *   onNavigationAttempt: navigationDialog.show,
 * });
 * ```
 */
export function useFormNavigation({
  isDirty,
  onNavigationAttempt,
}: {
  isDirty: boolean;
  onNavigationAttempt?: () => void;
}) {
  // Block in-app navigation (sidebar links, programmatic navigation)
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty]
    )
  );

  // Block browser navigation (refresh, close tab, back button)
  useBeforeUnload(
    useCallback(() => {
      if (isDirty) {
        return 'Aveți modificări nesalvate. Sigur doriți să părăsiți pagina?';
      }
    }, [isDirty])
  );

  // Trigger callback when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked' && onNavigationAttempt) {
      onNavigationAttempt();
    }
  }, [blocker.state, onNavigationAttempt]);

  return {
    blocker,
    isBlocked: blocker.state === 'blocked',
  };
}
